// VideoTutorialButton — eye-catching, clickbait-style CTA that opens an
// inline YouTube player in a Radix Dialog. The bare YouTube link is NEVER
// rendered into the DOM (no <a href="…youtube…">) — the URL stays inside
// the component state and is only injected into an <iframe> when the
// dialog mounts. This satisfies "the link should not show" while still
// letting the user watch the tutorial in-place.
//
// Visual design notes
// ───────────────────
//   • Sunset-orange gradient with a soft glowing ring → high contrast on
//     the otherwise sand-coloured Login/Register/Home pages.
//   • Animated "🔴 LIVE / ଭିଡିଓ" badge on the top-right corner — leans
//     into clickbait without misleading; this is a genuine tutorial.
//   • Hover lift + ring pulse via Tailwind transitions.
//   • Forces 16:9 iframe with `aspect-video` to avoid letter-boxing.
//
// Props
// ─────
//   • youtubeId        : 11-char YouTube video id (e.g. "VWrMfzv-81M")
//   • titleOdia        : large headline shown inside the button (Odia)
//   • subtitleOdia     : second line below the headline (Odia)
//   • testId           : test id for Playwright assertions
//   • variant          : "sunset" (default) | "moss" — colour palette
//   • dialogTitleOdia  : modal H2 title shown above the player
//   • durationLabel    : tiny chip (e.g. "▶ 3 ମିନଟ", "▶ 7 ମିନଟ")
//
// Usage
// ─────
//   <VideoTutorialButton
//     youtubeId="VWrMfzv-81M"
//     titleOdia="ଭିଡିଓ ଟ୍ୟୁଟୋରିଆଲ୍ ଦେଖନ୍ତୁ"
//     subtitleOdia="Account ତିଆରି କିପରି କରିବେ — Step by Step"
//     dialogTitleOdia="Account Creation Tutorial"
//     durationLabel="▶ 3 ମିନଟ"
//     testId="login-video-tutorial"
//   />

import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { PlayCircle, Sparkles } from "lucide-react";

const VARIANTS = {
  sunset: {
    btn: "bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white shadow-[0_10px_30px_-10px_rgba(244,114,33,0.7)] hover:shadow-[0_18px_40px_-12px_rgba(244,114,33,0.85)] ring-orange-400/40",
    badge: "bg-rose-600 text-white",
    pulse: "bg-orange-400/60",
    iconHalo: "bg-white/20 text-white",
  },
  moss: {
    btn: "bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white shadow-[0_10px_30px_-10px_rgba(20,184,166,0.7)] hover:shadow-[0_18px_40px_-12px_rgba(20,184,166,0.85)] ring-emerald-400/40",
    badge: "bg-emerald-700 text-white",
    pulse: "bg-emerald-400/60",
    iconHalo: "bg-white/20 text-white",
  },
};

export default function VideoTutorialButton({
  youtubeId,
  titleOdia,
  subtitleOdia,
  dialogTitleOdia,
  durationLabel = "▶ Video",
  testId = "video-tutorial-btn",
  variant = "sunset",
}) {
  const [open, setOpen] = useState(false);
  const v = VARIANTS[variant] || VARIANTS.sunset;

  // Build the embed URL only when the dialog opens, so the youtube link
  // never appears in the DOM before that. We DO NOT add `rel`/`href` — only
  // the `<iframe src>` carries the URL inside the modal.
  const embedSrc = open
    ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(youtubeId)}?autoplay=1&rel=0&modestbranding=1&playsinline=1`
    : "";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testId}
        className={`group relative isolate w-full overflow-hidden rounded-2xl px-5 py-4 text-left ring-2 transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0 ${v.btn}`}
      >
        {/* Animated pulsing ring */}
        <span aria-hidden className={`pointer-events-none absolute -inset-1 rounded-2xl opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-80 ${v.pulse}`} />
        {/* Live badge */}
        <span className={`absolute right-3 top-3 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-[0.18em] ${v.badge}`}>
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Live · {durationLabel}
        </span>
        <div className="flex items-center gap-4">
          <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-full ring-2 ring-white/40 transition-transform duration-200 group-hover:scale-110 ${v.iconHalo}`}>
            <PlayCircle className="h-7 w-7 fill-white/20" />
          </span>
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.22em] opacity-90">
              <Sparkles className="h-3 w-3" /> FREE Tutorial · ମାଗଣା
            </p>
            <p className="mt-1 font-heading text-lg font-extrabold leading-tight">
              {titleOdia}
            </p>
            {subtitleOdia && (
              <p className="mt-0.5 text-xs font-medium opacity-95">
                {subtitleOdia}
              </p>
            )}
          </div>
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid={`${testId}-dialog`}
          className="w-[calc(100vw-1.5rem)] max-w-3xl overflow-hidden p-0"
        >
          <DialogHeader className="border-b border-border bg-gradient-to-r from-clay/15 via-white to-moss/15 px-5 py-3">
            <DialogTitle className="font-heading text-base font-bold text-ink-900 sm:text-lg">
              🎬 {dialogTitleOdia || titleOdia}
            </DialogTitle>
            <DialogDescription className="text-xs text-ink-700">
              {subtitleOdia || "ତଳ ଭିଡିଓ ଦେଖନ୍ତୁ ଓ ସମ୍ପୂର୍ଣ୍ଣ ପ୍ରକ୍ରିୟା ଶିଖନ୍ତୁ"}
            </DialogDescription>
          </DialogHeader>
          <div className="aspect-video w-full bg-black">
            {open && (
              <iframe
                title={dialogTitleOdia || titleOdia}
                src={embedSrc}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="h-full w-full"
                data-testid={`${testId}-iframe`}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
